import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import shareService from '../services/ShareService';

/**
 * ShareModal — genera y comparte tarjetas de Concordare.
 *
 * Props:
 *   open, onClose
 *   topic         string  — nombre del tema
 *   topicDate     string  — fecha ISO "2026-03-04"
 *   userWords     Array<{raw: string, canonical: string}> — palabras enviadas por el usuario
 *   playersToday  number|null
 *
 *   mode='results'  — usa finalResults para scores, palabras visibles siempre
 *   finalResults    array  — requerido en modo 'results'
 *
 *   mode='pending'  — sin scores, palabras con toggle difuminado/visible
 */
export default function ShareModal({
  open,
  onClose,
  topic,
  topicDate,
  userWords,
  playersToday,
  finalResults,
  mode = 'results',
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [previewUrl, setPreviewUrl] = useState('');
  const [shareStatus, setShareStatus] = useState('idle');
  const [revealWords, setRevealWords] = useState(false);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);

  const isPending = mode === 'pending';

  useEffect(() => {
    if (!open) return;

    let canvas;
    if (isPending) {
      const params = { topic, topicDate, userWords: userWords || [], playersToday, revealWords };
      canvas = activeTab === 0
        ? shareService.drawPendingCompactCard(params)
        : shareService.drawPendingStoryCard(params);
    } else {
      const wordResults = shareService.computeWordResults(userWords, finalResults);
      const totalScore = shareService.computeTotalScore(wordResults);
      const coincidence = shareService.computeCoincidencePercent(wordResults, playersToday);
      const params = { topic, topicDate, wordResults, totalScore, playersToday, coincidence };
      canvas = activeTab === 0
        ? shareService.drawCompactCard(params)
        : shareService.drawStoryCard(params);
    }

    canvasRef.current = canvas;

    // Generar el File de forma SÍNCRONA usando toDataURL+atob para que fileRef.current
    // esté listo antes de que el usuario pulse el botón. canvas.toBlob() es asíncrono
    // y provoca una condición de carrera: si el usuario pulsa antes de que termine el
    // callback, fileRef.current es null y se pierde el contexto del gesto en móvil.
    const dateStr = String(topicDate || '').replaceAll('-', '');
    const suffix = isPending ? 'previa' : activeTab === 0 ? 'compacta' : 'historia';
    const filename = `concordare-${suffix}-${dateStr}.png`;
    try {
      const dataUrl = canvas.toDataURL('image/png');
      const base64 = dataUrl.split(',')[1];
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'image/png' });
      fileRef.current = new File([blob], filename, { type: 'image/png' });
    } catch {
      fileRef.current = null;
    }

    setPreviewUrl(shareService.getPreviewDataUrl(canvas));
    setShareStatus('idle');
  }, [open, activeTab, topic, topicDate, userWords, finalResults, playersToday, revealWords, isPending]);

  // Resetear estado al cerrar
  useEffect(() => {
    if (!open) {
      setActiveTab(0);
      setRevealWords(false);
      setPreviewUrl('');
    }
  }, [open]);

  const handleShare = async () => {
    if (!canvasRef.current) return;
    setShareStatus('sharing');
    const dateStr = String(topicDate || '').replaceAll('-', '');
    const suffix = isPending ? 'previa' : activeTab === 0 ? 'compacta' : 'historia';
    const filename = `concordare-${suffix}-${dateStr}.png`;
    try {
      const file = fileRef.current;

      // En apps nativas Capacitor usamos el plugin Share con Filesystem
      if (Capacitor.isNativePlatform() && file) {
        try {
          const reader = new FileReader();
          const base64 = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          await Filesystem.writeFile({
            path: filename,
            data: base64,
            directory: Directory.Cache,
          });
          const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
          await Share.share({ title: 'Concordare', files: [uri] });
          setShareStatus('idle');
          return;
        } catch (err) {
          if (err?.message?.includes('cancel') || err?.errorMessage?.includes('cancel')) {
            setShareStatus('idle');
            return;
          }
          // Caer al fallback web si algo falla
        }
      }

      // Web Share API (navegador web)
      if (file && navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        try {
          await navigator.share({ files: [file], title: 'Concordare' });
          setShareStatus('idle');
          return;
        } catch (err) {
          if (err.name === 'AbortError') { setShareStatus('idle'); return; }
        }
      }

      // Fallback: descarga directa (desktop)
      if (file) {
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
        setShareStatus('idle');
      } else {
        const result = await shareService.shareCard(canvasRef.current, filename);
        setShareStatus(result.method === 'cancelled' ? 'idle' : 'idle');
      }
    } catch {
      setShareStatus('error');
      setTimeout(() => setShareStatus('idle'), 2500);
    }
  };

  const handleCopyText = async () => {
    let text;
    if (isPending) {
      const playersLabel = playersToday
        ? t('share.playersLabel', { count: playersToday })
        : '';
      text = shareService.buildPendingClipboardText({
        topic, topicDate, userWords: userWords || [], playersLabel,
      });
    } else {
      const wordResults = shareService.computeWordResults(userWords, finalResults);
      const totalScore = shareService.computeTotalScore(wordResults);
      const playersLabel = playersToday
        ? t('share.playersLabel', { count: playersToday })
        : '';
      text = shareService.buildClipboardText({ topic, topicDate, wordResults, totalScore, playersLabel, playersToday });
    }
    await shareService.copyToClipboard(text);
    setShareStatus('copied');
    setTimeout(() => setShareStatus('idle'), 2500);
  };

  const isCompact = activeTab === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          {isPending ? t('share.titlePending') : t('share.title')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        centered
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={t('share.tabCompact')} />
        <Tab label={t('share.tabStory')} />
      </Tabs>

      <DialogContent sx={{ p: 2, bgcolor: '#f0f0f0' }}>
        {/* Toggle de palabras — solo en modo pending */}
        {isPending && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={revealWords}
                  onChange={(e) => setRevealWords(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {revealWords
                    ? <VisibilityIcon fontSize="small" />
                    : <VisibilityOffIcon fontSize="small" />
                  }
                  <Typography variant="body2">
                    {revealWords ? t('share.hideWords') : t('share.revealWords')}
                  </Typography>
                </Box>
              }
              sx={{ m: 0 }}
            />
          </Box>
        )}

        {/* Hint */}
        {!isPending && (
          <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mb={1.5}>
            {isCompact ? t('share.hintCompact') : t('share.hintStory')}
          </Typography>
        )}

        {/* Preview */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 180 }}>
          {previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt="preview"
              sx={{
                maxHeight: isCompact ? 300 : 440,
                maxWidth: '100%',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ) : (
            <CircularProgress size={32} />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2, pt: 1.5 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={
            shareStatus === 'sharing'
              ? <CircularProgress size={18} color="inherit" />
              : <ShareIcon />
          }
          disabled={shareStatus === 'sharing' || !previewUrl}
          onClick={handleShare}
          sx={{
            background: 'linear-gradient(90deg, #1976d2, #764ba2)',
            fontWeight: 700,
            py: 1.2,
            borderRadius: 2,
            '&:hover': { background: 'linear-gradient(90deg, #1565c0, #6a1b9a)' },
            '&.Mui-disabled': { opacity: 0.6 },
          }}
        >
          {t('share.shareButton')}
        </Button>

        <Button
          variant="outlined"
          fullWidth
          startIcon={shareStatus === 'copied' ? <CheckIcon /> : <ContentCopyIcon />}
          onClick={handleCopyText}
          color={shareStatus === 'copied' ? 'success' : 'primary'}
          sx={{ py: 1, borderRadius: 2 }}
        >
          {shareStatus === 'copied' ? t('share.copied') : t('share.copyText')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
